import { ChangeEvent, DragEvent, useRef, useState } from "react"
import { useToaster } from "@hellocontento/maillard"
import _ from "lodash"

import { callApi } from "utils/ContentoApi"
import { Picture } from "./types"
import { useRotatingMessage } from "./useRotatingMessage"
import { uploadFileToSignedUrl } from "./visualProfile"

// Must match the backend's accepted picture types.
const ACCEPTED_PICTURE_TYPES = "image/png,image/jpeg,image/webp"
// Registration takes ~2.5 s per picture; small requests stay far below the 60 s ingress timeout.
const REGISTER_PICTURES_CHUNK_SIZE = 5

const UPLOAD_STATUS_MESSAGES = [
  "Uploading pictures…",
  "Analyzing pictures…",
  "Detecting faces…",
  "Measuring the layout…",
  "Checking logo contrast…",
  "Almost there…",
]

const isAcceptedPictureFile = (file: File) => ACCEPTED_PICTURE_TYPES.split(",").includes(file.type)

type PictureUploadParams = {
  accountId: string
  profileId: string
  disabled?: boolean // dropped and picked files are ignored while true, as they are while an upload runs
  onUploadStarted?: () => void
  onPicturesAdded: (pictures: Picture[]) => void // newest first like the list endpoint; called once per registered chunk, not once per upload
}

export const usePictureUpload = ({ accountId, profileId, disabled = false, onUploadStarted, onPicturesAdded }: PictureUploadParams) => {
  const addToast = useToaster()
  const [uploadingCount, setUploadingCount] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const statusMessage = useRotatingMessage(UPLOAD_STATUS_MESSAGES, uploadingCount > 0)
  // One upload at a time: a second one would reset the count while the first is still finishing.
  const acceptsFiles = !disabled && uploadingCount === 0

  const uploadFiles = async (files: File[]) => {
    if (!acceptsFiles || files.length === 0) return
    const acceptedFiles = files.filter(isAcceptedPictureFile)
    if (acceptedFiles.length === 0) {
      addToast("Only PNG, JPEG and WebP pictures can be added", "error")
      return
    }

    onUploadStarted?.()
    setUploadingCount(acceptedFiles.length)
    try {
      const { uploads } = await callApi(
        {
          method: "post",
          url: `content-factory/accounts/${accountId}/profiles/${profileId}/pictures/upload-urls`,
          data: { contentTypes: acceptedFiles.map((file) => file.type) },
        },
        true,
        true,
      )

      const uploadOutcomes = await Promise.allSettled(acceptedFiles.map((file, index) => uploadFileToSignedUrl(uploads[index].signedUrl, file)))
      const uploadedUrls = uploads
        .filter((_upload: unknown, index: number) => uploadOutcomes[index].status === "fulfilled")
        .map((upload: { publicUrl: string }) => upload.publicUrl)
      let failedPictureCount = acceptedFiles.length - uploadedUrls.length
      setUploadingCount(uploadedUrls.length)

      for (const urls of _.chunk(uploadedUrls, REGISTER_PICTURES_CHUNK_SIZE)) {
        try {
          const { pictures: createdPictures, failures } = await callApi(
            {
              method: "post",
              url: `content-factory/accounts/${accountId}/profiles/${profileId}/pictures`,
              data: { urls },
            },
            true,
            true,
          )
          onPicturesAdded([...createdPictures].reverse())
          for (const failure of failures) {
            addToast(`Failed to add a picture: ${failure.reason}`, "error")
          }
        } catch (err) {
          console.error("Failed to register pictures", { accountId, profileId, urls, err })
          failedPictureCount += urls.length
        } finally {
          setUploadingCount((count) => count - urls.length)
        }
      }

      if (failedPictureCount > 0) {
        addToast(`Failed to upload ${failedPictureCount} picture${failedPictureCount > 1 ? "s" : ""}`, "error")
      }
    } catch (err) {
      console.error("Failed to upload pictures", { accountId, profileId, err })
      addToast("Failed to upload pictures", "error")
    } finally {
      setUploadingCount(0)
    }
  }

  const handleFilesChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? [])
    // Reset the input value immediately so re-selecting the same files
    // still triggers an onChange.
    event.target.value = ""
    uploadFiles(files)
  }

  const dropZoneProps = {
    // preventDefault stays unconditional: without it the browser opens a file dropped during an upload.
    onDragOver: (event: DragEvent) => {
      event.preventDefault()
      if (acceptsFiles) setIsDragging(true)
    },
    // Moving onto a child fires dragleave on the zone as well; only leaving the zone itself ends the drag.
    onDragLeave: (event: DragEvent) => {
      if (event.currentTarget.contains(event.relatedTarget as Node | null)) return
      setIsDragging(false)
    },
    onDrop: (event: DragEvent) => {
      event.preventDefault()
      setIsDragging(false)
      uploadFiles(Array.from(event.dataTransfer.files))
    },
  }

  const fileInputProps = {
    ref: fileInputRef,
    type: "file",
    multiple: true,
    accept: ACCEPTED_PICTURE_TYPES,
    style: { display: "none" },
    onChange: handleFilesChange,
    "aria-hidden": true,
  }

  const openFilePicker = () => fileInputRef.current?.click()

  return { uploadingCount, statusMessage, isDragging, dropZoneProps, fileInputProps, openFilePicker }
}
